/**
 * Which model every AUTOMATED enemy gets (02 §9.2, ROUND4 §1): the emplacement machines and the
 * four vehicles. Kept out of `world/Machines` so the table can be read by the turntable and checked
 * by a test without the renderer, and so that adding one of VEH's kinds is one line here.
 *
 * `isAutomated(kind)` is the sim's own list; `fieldVehicles.test.ts` fails if a kind is on it and
 * has no entry here, because the alternative is `Actors` quietly building a Soldier rig on a lorry.
 *
 * The three vehicles that already had a parked prop model reuse it, wreck and all. Only `truck` is
 * new. The train's models are the track-free pair (`rail.ts`), because nothing lays rails under a
 * vehicle that is driving a route.
 */

import type { EnemyKind } from '../../sim/types';
import { mobileLauncher, mobileLauncherLamps, staffCar, staffCarLamps } from '../kit/convoy';
import type { KitBuilder } from '../kit/flora';
import { miniBunkerBody, miniBunkerSlot, miniBunkerWreck, minitankBody, minitankWreck } from '../kit/machines';
import { railEngine, railEngineLamp } from '../kit/rail';
import { truck, truckEmpty, truckLamps, truckWreck } from '../kit/truck';
import { mobileLauncherWreck, railEngineWreck, staffCarWreck } from '../kit/wrecksVehicles';

export interface MachineModel {
  body: KitBuilder;
  /** Swapped in on `state === 'dead'`, over the same spot: VEH's wrecks block the road. */
  wreck: KitBuilder;
  /**
   * The self-lit part, or null. The mini-bunker's sensor slot burns whenever it is LIVE, whatever
   * the hour; a vehicle's headlamps only after dark. `Machines.lampsOn` says which.
   */
  lamps: KitBuilder | null;
  /**
   * The model to draw when nobody is aboard (`Enemy.vehicle.aboard === 0`). Only the troop carrier
   * has one: its canvas is laced shut over a load and rolled forward over bare hoops when empty, so
   * a player can pick which lorry to mine (mission 34) instead of guessing.
   */
  unladen?: KitBuilder;
}

export const MACHINE_MODELS: Partial<Record<EnemyKind, MachineModel>> = {
  miniBunker: { body: miniBunkerBody, wreck: miniBunkerWreck, lamps: miniBunkerSlot },
  minitank: { body: minitankBody, wreck: minitankWreck, lamps: null },
  truck: { body: truck, wreck: truckWreck, lamps: truckLamps, unladen: truckEmpty },
  staffCar: { body: staffCar, wreck: staffCarWreck, lamps: staffCarLamps },
  railEngine: { body: railEngine, wreck: railEngineWreck, lamps: railEngineLamp },
  launcherTruck: { body: mobileLauncher, wreck: mobileLauncherWreck, lamps: mobileLauncherLamps },
};

/**
 * The model for a machine kind. The fallback is the minitank's, so a kind VEH adds to `isAutomated`
 * before it is modelled here is drawn as SOMETHING rigid rather than as a rifleman standing in the
 * road - and the test says which kind is missing.
 */
export function machineModel(kind: EnemyKind): MachineModel {
  return MACHINE_MODELS[kind] ?? { body: minitankBody, wreck: minitankWreck, lamps: null };
}
